import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, ChevronDown, ChevronUp, Mail, Phone, Loader2, FileText, Download, MessageSquare, LifeBuoy, Pause, Play, CreditCard } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ChatThread from "../components/ChatThread";
import { DocButton } from "../components/DocViewer";
import api from "../api";
import { useAuth } from "../context/AuthContext";

const STATUS = {
  pending_payment: { label: "Awaiting activation", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  pending_review: { label: "In review", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  live: { label: "Live", cls: "bg-green-50 text-green-700 border-green-200" },
  rejected: { label: "Rejected", cls: "bg-red-50 text-red-700 border-red-200" },
};

export default function FamilyDashboard() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [apps, setApps] = useState({});
  const [chatApp, setChatApp] = useState(null);
  const [supportOpen, setSupportOpen] = useState(false);
  const [membership, setMembership] = useState(null);
  const [memBusy, setMemBusy] = useState(false);

  useEffect(() => {
    api.get("/jobs/mine").then(({ data }) => setJobs(data)).finally(() => setLoading(false));
    api.get("/membership").then(({ data }) => setMembership(data)).catch(() => {});
  }, []);

  const toggleMembership = async () => {
    if (!membership || memBusy) return;
    setMemBusy(true);
    try {
      const url = membership.paused ? "/membership/resume" : "/membership/pause";
      const { data } = await api.post(url);
      setMembership((m) => ({ ...m, paused: data.paused }));
      toast.success(data.paused ? "Membership paused — you won't be billed while paused." : "Membership resumed.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update membership.");
    } finally { setMemBusy(false); }
  };

  const toggle = async (job) => {
    if (open === job.id) { setOpen(null); return; }
    setOpen(job.id);
    if (!apps[job.id] && job.status === "live") {
      try {
        const { data } = await api.get(`/jobs/${job.id}/applications`);
        setApps((p) => ({ ...p, [job.id]: data }));
      } catch (e) { /* ignore */ }
    }
  };

  return (
    <div>
      <Header />
      <div className="bg-[#fdfaf7] min-h-[70vh]">
        <div className="max-w-5xl mx-auto px-5 py-12">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="font-display text-3xl text-[#1f1a1b]">Welcome, {user?.name?.split(" ")[0] || "there"}</h1>
              <p className="text-[15px] text-[#5a5254] mt-1">Manage your jobs and applications.</p>
            </div>
            <Link to="/post-job" className="inline-flex items-center gap-2 rounded-xl bg-[#7c2233] hover:bg-[#661b29] text-white font-medium px-5 py-3 transition-colors">
              <Plus className="w-4 h-4" /> Post a new job
            </Link>
          </div>

          <div className="mt-6 rounded-2xl border border-black/10 bg-white overflow-hidden">
            <button onClick={() => setSupportOpen(!supportOpen)} className="w-full flex items-center justify-between px-5 py-4" data-testid="family-support-toggle">
              <span className="flex items-center gap-2 font-display text-lg text-[#1f1a1b]"><LifeBuoy className="w-5 h-5 text-[#7c2233]" /> Message support</span>
              {supportOpen ? <ChevronUp className="w-5 h-5 text-[#7c2233]" /> : <ChevronDown className="w-5 h-5 text-[#7c2233]" />}
            </button>
            {supportOpen && (
              <div className="px-5 pb-5">
                <p className="text-[13px] text-[#8a8285] mb-2">Questions or need a hand? Chat with the SimplySitter team here.</p>
                <ChatThread threadType="support" threadId={user?.id} meId={user?.id} placeholder="Message the SimplySitter team…" />
              </div>
            )}
          </div>

          {membership && (
            <div className="mt-4 rounded-2xl border border-black/10 bg-white px-5 py-4 flex items-center justify-between gap-4 flex-wrap" data-testid="family-membership-card">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-[#f7e4e6] flex items-center justify-center shrink-0"><CreditCard className="w-4.5 h-4.5 text-[#7c2233]" /></div>
                <div>
                  {membership.has_subscription ? (
                    <>
                      <p className="font-medium text-[#1f1a1b] text-[15px]">Membership {membership.paused ? "— paused" : "— active"}</p>
                      <p className="text-[13px] text-[#8a8285] max-w-xl">{membership.paused ? "Billing is paused — you're not being charged. Resume anytime; you keep your account and there's no new free month." : "$15.99/month. Pause anytime — you won't be billed while paused, and resuming does not restart the free month."}</p>
                    </>
                  ) : (
                    <>
                      <p className="font-medium text-[#1f1a1b] text-[15px]">Membership — not active yet</p>
                      <p className="text-[13px] text-[#8a8285] max-w-xl">Activate a job to start your membership (1st month free). Afterwards you can <strong>pause</strong> anytime here instead of cancelling — pausing stops billing, and resuming never gives a new free month.</p>
                    </>
                  )}
                </div>
              </div>
              {membership.has_subscription ? (
                <button onClick={toggleMembership} disabled={memBusy} data-testid="family-membership-toggle"
                  className={`inline-flex items-center gap-1.5 text-[14px] font-medium rounded-lg px-4 py-2 transition-colors disabled:opacity-60 ${membership.paused ? "text-white bg-[#7c2233] hover:bg-[#661b29]" : "text-[#7c2233] border border-[#7c2233]/30 hover:bg-[#faf1f2]"}`}>
                  {memBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : membership.paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                  {membership.paused ? "Resume membership" : "Pause membership"}
                </button>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-[13px] text-[#8a8285] border border-black/10 rounded-lg px-3 py-2"><Pause className="w-3.5 h-3.5" /> Pause available after activation</span>
              )}
            </div>
          )}

          {loading ? (
            <div className="flex items-center gap-2 text-[#7c2233] mt-10"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
          ) : jobs.length === 0 ? (
            <div className="mt-10 rounded-2xl border border-black/10 bg-white p-10 text-center">
              <p className="text-[15px] text-[#5a5254]">You haven't posted any jobs yet.</p>
              <Link to="/post-job" className="inline-flex mt-4 rounded-xl bg-[#7c2233] text-white font-medium px-5 py-3">Post your first job</Link>
            </div>
          ) : (
            <div className="mt-8 space-y-4">
              {jobs.map((j) => {
                const st = STATUS[j.status] || STATUS.pending_review;
                const jobApps = apps[j.id] || [];
                return (
                  <div key={j.id} className="rounded-2xl border border-black/10 bg-white overflow-hidden">
                    <div className="p-5 flex items-center justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-display text-xl text-[#1f1a1b] capitalize">{j.service}</h3>
                          <span className={`text-[12px] font-medium px-2.5 py-0.5 rounded-full border ${st.cls}`}>{st.label}</span>
                          {j.applications_count > 0 && (
                            <span data-testid={`job-applications-badge-${j.id}`} className="inline-flex items-center gap-1 text-[12px] font-semibold px-2.5 py-0.5 rounded-full bg-[#7c2233] text-white">
                              {j.applications_count} application{j.applications_count === 1 ? "" : "s"}
                            </span>
                          )}
                        </div>
                        <p className="text-[14px] text-[#5a5254] mt-1">{j.location}{j.details ? ` — ${j.details}` : ""}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        {j.status === "pending_payment" && (
                          <Link to={`/checkout/${j.id}`} className="text-[14px] font-medium text-white bg-[#7c2233] rounded-lg px-4 py-2">Activate</Link>
                        )}
                        <button onClick={() => toggle(j)} data-testid={`view-applications-${j.id}`}
                          className="inline-flex items-center gap-1.5 text-[14px] font-medium text-[#7c2233] border border-[#7c2233]/30 rounded-lg px-3 py-2 hover:bg-[#faf1f2] transition-colors">
                          {j.status === "live" ? `View applications${j.applications_count ? ` (${j.applications_count})` : ""}` : "Details"}
                          {open === j.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    {open === j.id && (
                      <div className="border-t border-black/[0.06] bg-[#fdfaf7] p-5">
                        {j.status !== "live" ? (
                          <p className="text-[14px] text-[#8a8285]">Applications appear here once your job is live.</p>
                        ) : jobApps.length === 0 ? (
                          <p className="text-[14px] text-[#8a8285]">No applications yet. We'll email you when one arrives.</p>
                        ) : (
                          <div className="space-y-3">
                            {jobApps.map((a) => (
                              <div key={a.id} className="rounded-xl border border-black/10 bg-white p-4">
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                  <div>
                                    <p className="font-medium text-[#1f1a1b]">{a.caregiver_name}</p>
                                    {a.caregiver_bio && <p className="text-[13px] text-[#5a5254] mt-1 max-w-xl">{a.caregiver_bio}</p>}
                                    {a.message && <p className="text-[14px] text-[#3a3436] mt-1.5 italic">“{a.message}”</p>}
                                    <div className="flex flex-wrap gap-4 mt-2 text-[13px] text-[#7c2233]">
                                      <a href={`mailto:${a.caregiver_email}`} className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{a.caregiver_email}</a>
                                      {a.caregiver_phone && <span className="flex items-center gap-1.5 text-[#5a5254]"><Phone className="w-3.5 h-3.5" />{a.caregiver_phone}</span>}
                                    </div>
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    <DocButton url={a.caregiver_resume} label="Resume" icon={FileText} testid={`family-view-resume-${a.id}`} className="inline-flex items-center gap-1.5 text-[13px] text-[#7c2233] border border-[#7c2233]/30 rounded-lg px-3 py-1.5 hover:bg-[#faf1f2]" />
                                    <DocButton url={a.caregiver_id_doc} label="ID" icon={Download} testid={`family-view-id-${a.id}`} className="inline-flex items-center gap-1.5 text-[13px] text-[#7c2233] border border-[#7c2233]/30 rounded-lg px-3 py-1.5 hover:bg-[#faf1f2]" />
                                    <button onClick={() => setChatApp(chatApp === a.id ? null : a.id)} className="inline-flex items-center gap-1.5 text-[13px] text-white bg-[#7c2233] hover:bg-[#661b29] rounded-lg px-3 py-1.5 transition-colors"><MessageSquare className="w-3.5 h-3.5" /> Message</button>
                                  </div>
                                </div>
                                {chatApp === a.id && (
                                  <div className="mt-3">
                                    <ChatThread threadType="application" threadId={a.id} meId={user?.id} placeholder={`Message ${a.caregiver_name.split(" ")[0]}…`} />
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
